import { computed, ref } from 'vue'

import { getCachedLocalStorage } from './useAppStorage'

const THEME_STORAGE_KEY = 'spatius-admin-theme'

type ThemeMode = 'light' | 'dark' | 'system'

const themeMode = ref<ThemeMode>('system')
const systemPrefersDark = ref(false)
let initialized = false

const isDark = computed(() => {
  if (themeMode.value === 'system')
    return systemPrefersDark.value

  return themeMode.value === 'dark'
})

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system'
}

function applyTheme() {
  if (typeof document === 'undefined')
    return

  const root = document.documentElement
  const theme = isDark.value ? 'dark' : 'light'

  root.classList.toggle('dark', isDark.value)
  root.setAttribute('data-kb-theme', theme)
  root.style.colorScheme = theme
}

function persistTheme() {
  getCachedLocalStorage<ThemeMode>(THEME_STORAGE_KEY, 'system').value = themeMode.value
}

function setTheme(mode: ThemeMode) {
  themeMode.value = mode
  persistTheme()
  applyTheme()
}

function toggleTheme() {
  setTheme(isDark.value ? 'light' : 'dark')
}

function initializeTheme() {
  if (initialized)
    return

  initialized = true

  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mediaQuery.matches

    const storedTheme = getCachedLocalStorage<ThemeMode | string>(THEME_STORAGE_KEY, 'system')
    if (isThemeMode(storedTheme.value)) {
      themeMode.value = storedTheme.value
    }
    else {
      storedTheme.value = 'system'
    }

    mediaQuery.addEventListener('change', (event) => {
      systemPrefersDark.value = event.matches
      applyTheme()
    })
  }

  applyTheme()
}

export function useTheme() {
  initializeTheme()

  return {
    isDark,
    setTheme,
    themeMode,
    toggleTheme,
  }
}
